import { Link } from 'react-router-dom'
import {Phone, Clock, ShieldCheck, Award, CheckCircle2, Droplets, Trash2, Zap, Flame, ArrowRight, Star, MapPin, Quote, Calendar, Thermometer, Wrench, Sparkles, ChevronRight} from 'lucide-react'
import ContactForm from '@/components/ContactForm'
import SectionHeader from '@/components/SectionHeader'
import FixNowBadge from '@/components/FixNowBadge'

const heroImage = 'https://static.lumi.new/b6/b6b6566c35e89aeb26cefb51a53709a8.png'

const emergencyServices = [
  { icon: Droplets, title: "Fuite d'eau", desc: "Détection et réparation rapide de toute fuite, jour et nuit." },
  { icon: Trash2, title: "WC bouché", desc: "Débouchage rapide sans dégâts, intervention sous 1h." },
  { icon: Zap, title: "Canalisation", desc: "Débouchage haute pression et caméra d'inspection." },
  { icon: Flame, title: "Chauffe-eau", desc: "Dépannage et remplacement de chauffe-eau toutes marques." },
  { icon: Thermometer, title: "Chauffage", desc: "Dépannage chaudière, radiateurs froids, perte de pression." },
]

const pillars = [
  {
    eyebrow: '01 — Précision',
    title: 'Diagnostic juste, du premier coup.',
    desc: "Détection ciblée, outils calibrés, intervention sans approximation. On ne devine pas — on mesure, on identifie, on répare.",
    icon: Sparkles,
  },
  {
    eyebrow: '02 — Fiabilité',
    title: 'Réponse en 35 min en moyenne.',
    desc: "Disponibilité 24/7 jours fériés inclus. Plombiers et chauffagistes certifiés, assurés RC pro, équipés pour les urgences les plus critiques.",
    icon: ShieldCheck,
  },
  {
    eyebrow: '03 — Engagement',
    title: 'Devis annoncé, devis respecté.',
    desc: "Tarif transparent communiqué avant toute intervention. Aucune dérive, aucun surcoût caché. Garantie sur tous nos travaux.",
    icon: Award,
  },
]

const zones = [
  { city: 'Genève', slug: 'geneve' },
  { city: 'Lausanne', slug: 'lausanne' },
  { city: 'Montreux', slug: 'montreux' },
  { city: 'Vevey', slug: 'vevey' },
  { city: 'Nyon', slug: 'nyon' },
  { city: 'Neuchâtel', slug: 'neuchatel' },
  { city: 'Fribourg', slug: 'fribourg' },
  { city: 'Sion', slug: 'sion' },
]

const testimonials = [
  {
    name: 'Marie L.',
    city: 'Genève',
    role: 'Particulier',
    text: "Fuite d'eau à 23h un dimanche. Intervention en 40 minutes, problème résolu, prix annoncé respecté. Très professionnel.",
    rating: 5,
  },
  {
    name: 'David R.',
    city: 'Lausanne',
    role: 'Particulier',
    text: "Chauffe-eau remplacé le jour même. Technicien ponctuel, propre et compétent. Je recommande sans hésiter.",
    rating: 5,
  },
  {
    name: 'Régie Alpina',
    city: 'Montreux',
    role: 'Régie immobilière',
    text: "Partenaire de confiance pour notre parc immobilier. Réactivité exemplaire et facturation claire.",
    rating: 5,
  },
]

const services = [
  {
    icon: Zap,
    title: 'Dépannage urgence',
    desc: '24/7 — fuite, débouchage, chauffe-eau, chauffage.',
    href: '/depannage-urgence',
  },
  {
    icon: Wrench,
    title: 'Entretien & maintenance',
    desc: 'Contrats annuels, détartrage, prévention des pannes.',
    href: '/entretien-maintenance',
  },
  {
    icon: Sparkles,
    title: 'Installations sanitaires',
    desc: 'Pose, rénovation, remplacements, raccordements.',
    href: '/installations',
  },
]

export default function Home() {
  return (
    <div className="bg-ink">
      {/* HERO — Premium Dark Metallic */}
      <section className="relative overflow-hidden bg-ink">
        {/* Background layers */}
        <div className="absolute inset-0 bg-grid opacity-60" aria-hidden="true" />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(ellipse 80% 50% at 70% 0%, rgba(220,38,38,0.18) 0%, transparent 60%)',
          }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(ellipse 60% 40% at 10% 100%, rgba(63,63,70,0.5) 0%, transparent 60%)',
          }}
          aria-hidden="true"
        />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-14 pb-20 lg:pt-24 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* LEFT — copy */}
            <div className="lg:col-span-7 animate-fade-up">
              {/* Status bar */}
              <div className="flex flex-wrap items-center gap-2.5">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-urgent-600/10 border border-urgent-500/30 backdrop-blur text-urgent-400 text-[11px] font-bold uppercase tracking-[0.18em]">
                  <span className="relative flex w-2 h-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-urgent-500 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-urgent-500" />
                  </span>
                  Disponible 24h/24 — 7j/7
                </div>
                <FixNowBadge variant="compact" />
              </div>

              {/* Signature */}
              <div className="mt-7 flex items-center gap-3 text-[10px] sm:text-xs font-bold uppercase tracking-[0.32em] text-steel">
                <span className="w-8 h-px bg-urgent-500" />
                <span>Précision</span>
                <span className="w-1 h-1 rounded-full bg-steel/50" />
                <span>Fiabilité</span>
                <span className="w-1 h-1 rounded-full bg-steel/50" />
                <span>Engagement</span>
              </div>

              {/* Headline */}
              <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-[64px] xl:text-[72px] font-extrabold text-white leading-[1.02] tracking-tight">
                Dépannage{' '}
                <span className="text-metal">sanitaire</span>
                <br className="hidden sm:block" />
                {' '}& chauffage premium en{' '}
                <span className="relative inline-block">
                  <span className="relative z-10 text-white">Suisse romande</span>
                  <span className="absolute left-0 right-0 -bottom-1 lg:-bottom-2 h-[3px] bg-urgent-600" aria-hidden="true" />
                </span>
                .
              </h1>

              <p className="mt-7 text-base lg:text-lg text-steel leading-relaxed max-w-xl">
                Fuite, WC bouché, chauffe-eau ou chaudière en panne ? Les techniciens certifiés <span className="text-white font-semibold">NEXACO Sàrl</span> interviennent en moins d'une heure à Genève, Lausanne, Vaud, Neuchâtel, Fribourg et Valais.
              </p>

              {/* CTAs */}
              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <a
                  href="tel:+41796430399"
                  className="group relative inline-flex items-center justify-center gap-2.5 px-7 py-4 rounded-xl gradient-urgent text-white font-bold shadow-orange-glow hover:scale-[1.02] active:scale-[0.98] transition-transform"
                >
                  <Phone className="w-5 h-5" strokeWidth={2.5} />
                  Appeler maintenant
                  <span className="hidden sm:inline text-white/70 font-mono text-sm tracking-wider ml-1">079 643 03 99</span>
                </a>
                <a
                  href="https://fixnow.ch"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2.5 px-7 py-4 rounded-xl bg-white/5 hover:bg-white/10 backdrop-blur border border-white/15 hover:border-white/30 text-white font-bold transition-all"
                >
                  <Calendar className="w-5 h-5" strokeWidth={2.5} />
                  Voir notre disponibilité sur FixNow
                  <ArrowRight className="w-4 h-4 opacity-60 group-hover:translate-x-0.5 transition-transform" />
                </a>
              </div>

              {/* Bullets */}
              <div className="mt-10 grid grid-cols-3 gap-4 sm:gap-6 max-w-xl border-t border-white/10 pt-7">
                <div>
                  <div className="text-2xl sm:text-3xl font-extrabold text-white font-display">35<span className="text-urgent-500">′</span></div>
                  <div className="text-[10px] sm:text-xs text-steel mt-1 font-semibold uppercase tracking-wider">Délai moyen</div>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-extrabold text-white font-display">5<span className="text-urgent-500">k</span>+</div>
                  <div className="text-[10px] sm:text-xs text-steel mt-1 font-semibold uppercase tracking-wider">Interventions</div>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-extrabold text-white font-display">4.9<span className="text-urgent-500">/5</span></div>
                  <div className="text-[10px] sm:text-xs text-steel mt-1 font-semibold uppercase tracking-wider">320+ avis</div>
                </div>
              </div>
            </div>

            {/* RIGHT — visual */}
            <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: '0.15s' }}>
              <div className="relative">
                {/* Decorative ring */}
                <div className="absolute -inset-4 rounded-[32px] bg-gradient-to-br from-urgent-600/30 via-transparent to-white/5 blur-2xl opacity-60" aria-hidden="true" />

                <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-elevated bg-ink-card">
                  <img
                    src={heroImage}
                    alt="Technicien NEXACO intervenant en Suisse romande"
                    className="w-full h-[460px] lg:h-[600px] object-cover"
                    loading="eager"
                  />
                  {/* Top overlay rating */}
                  <div className="absolute top-5 left-5 right-5 flex items-start justify-between gap-3">
                    <div className="bg-black/60 backdrop-blur-md rounded-xl px-3.5 py-2.5 border border-white/10">
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-3.5 h-3.5 fill-urgent-500 text-urgent-500" />
                        ))}
                      </div>
                      <div className="text-[11px] font-bold text-white mt-1">4.9/5 · 320+ avis</div>
                    </div>
                    <div className="bg-urgent-600 rounded-xl px-3.5 py-2.5 shadow-orange-glow">
                      <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-white/80">Délai moyen</div>
                      <div className="text-lg font-extrabold text-white font-display leading-none mt-1">35 min</div>
                    </div>
                  </div>

                  {/* Bottom phone card */}
                  <div className="absolute bottom-5 left-5 right-5">
                    <a
                      href="tel:+41796430399"
                      className="flex items-center gap-3 bg-black/70 backdrop-blur-md hover:bg-black/80 rounded-xl p-3.5 border border-white/10 hover:border-urgent-500/40 transition-all group"
                    >
                      <div className="w-11 h-11 rounded-xl gradient-urgent flex items-center justify-center flex-shrink-0 animate-pulse-ring">
                        <Phone className="w-5 h-5 text-white" strokeWidth={2.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-urgent-400">Urgence 24/7</div>
                        <div className="text-lg font-extrabold text-white font-display leading-tight mt-0.5">079 643 03 99</div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-white/40 group-hover:text-white group-hover:translate-x-0.5 transition-all" />
                    </a>
                  </div>

                  {/* Subtle gradient overlay for legibility */}
                  <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/50 via-transparent to-black/30" aria-hidden="true" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Marquee partners / trust strip */}
        <div className="relative border-t border-white/10 bg-black/40">
          <div className="max-w-7xl mx-auto px-5 lg:px-8 py-5 flex flex-wrap items-center justify-center lg:justify-between gap-x-8 gap-y-3 text-[11px] font-bold uppercase tracking-[0.22em] text-steel/70">
            <span className="text-steel">Ils nous font confiance</span>
            <span>Régies immobilières</span>
            <span className="hidden sm:block w-1 h-1 rounded-full bg-steel/40" />
            <span>Syndics</span>
            <span className="hidden sm:block w-1 h-1 rounded-full bg-steel/40" />
            <span>PME & commerces</span>
            <span className="hidden sm:block w-1 h-1 rounded-full bg-steel/40" />
            <span>Particuliers</span>
          </div>
        </div>
      </section>

      {/* PILLARS — Précision · Fiabilité · Engagement */}
      <section className="relative bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.25em] text-urgent-600">
              <span className="w-8 h-px bg-urgent-600" />
              Notre signature
            </div>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-800 tracking-tight leading-[1.05]">
              Trois engagements,<br />une seule promesse&nbsp;: <span className="text-urgent-600">la tranquillité.</span>
            </h2>
            <p className="mt-5 text-base lg:text-lg text-navy-500 leading-relaxed">
              Nous ne vendons pas de la plomberie. Nous vendons de la réactivité, de la précision et de la confiance — quand chaque minute compte.
            </p>
          </div>

          <div className="mt-14 lg:mt-20 grid md:grid-cols-3 gap-px bg-navy-100 rounded-3xl overflow-hidden border border-navy-100">
            {pillars.map((p) => (
              <div key={p.eyebrow} className="bg-white p-7 lg:p-9 group hover:bg-navy-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-urgent-600">
                    {p.eyebrow}
                  </div>
                  <div className="w-10 h-10 rounded-full bg-navy-900 flex items-center justify-center group-hover:bg-urgent-600 transition-colors">
                    <p.icon className="w-4.5 h-4.5 text-white" strokeWidth={2} />
                  </div>
                </div>
                <h3 className="mt-7 font-display text-xl lg:text-2xl font-extrabold text-navy-800 leading-tight tracking-tight">
                  {p.title}
                </h3>
                <p className="mt-3 text-sm lg:text-[15px] text-navy-500 leading-relaxed">
                  {p.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW — 3 grandes lignes */}
      <section className="relative bg-navy-50 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12 lg:mb-16">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.25em] text-urgent-600">
                <span className="w-8 h-px bg-urgent-600" />
                Nos métiers
              </div>
              <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-800 tracking-tight leading-[1.05]">
                Une expertise complète,<br />sanitaire et chauffage.
              </h2>
            </div>
            <Link to="/zones-intervention" className="hidden lg:inline-flex items-center gap-1.5 text-sm font-bold text-navy-700 hover:text-urgent-600 transition-colors">
              Voir nos zones d'intervention <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {services.map((s) => (
              <Link
                key={s.title}
                to={s.href}
                className="group relative bg-white rounded-3xl p-7 lg:p-8 border border-navy-100 hover:border-navy-800 hover:shadow-elevated transition-all overflow-hidden"
              >
                <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-urgent-50 group-hover:bg-urgent-100 transition-colors" aria-hidden="true" />
                <div className="relative">
                  <div className="w-14 h-14 rounded-2xl gradient-trust flex items-center justify-center mb-6 group-hover:scale-105 transition-transform">
                    <s.icon className="w-6 h-6 text-white" strokeWidth={2.2} />
                  </div>
                  <h3 className="font-display text-xl lg:text-2xl font-extrabold text-navy-800 tracking-tight">{s.title}</h3>
                  <p className="mt-2 text-sm lg:text-[15px] text-navy-500 leading-relaxed">{s.desc}</p>
                  <div className="mt-7 inline-flex items-center gap-1.5 text-sm font-bold text-urgent-600">
                    Découvrir <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* EMERGENCY SERVICES — dark feature grid */}
      <section className="relative gradient-trust text-white py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-40" aria-hidden="true" />
        <div
          className="absolute inset-0"
          style={{ backgroundImage: 'radial-gradient(ellipse 60% 50% at 90% 50%, rgba(220,38,38,0.15) 0%, transparent 60%)' }}
          aria-hidden="true"
        />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-urgent-600/15 border border-urgent-500/30 text-urgent-400 text-[11px] font-bold uppercase tracking-[0.22em]">
              <span className="w-1.5 h-1.5 rounded-full bg-urgent-500 animate-pulse" />
              Urgence 24/7
            </div>
            <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05]">
              Nos <span className="text-metal">interventions d'urgence</span>
            </h2>
            <p className="mt-5 text-base lg:text-lg text-steel leading-relaxed">
              Une équipe disponible immédiatement pour résoudre vos pannes sanitaires et de chauffage les plus critiques.
            </p>
          </div>

          <div className="mt-14 lg:mt-20 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {emergencyServices.map((s, i) => (
              <Link
                key={s.title}
                to="/depannage-urgence"
                className="group relative bg-white/[0.03] hover:bg-white/[0.06] backdrop-blur rounded-2xl p-6 border border-white/10 hover:border-urgent-500/40 transition-all"
                style={{ animationDelay: `${i * 0.05}s` }}
              >
                <div className="absolute top-5 right-5 text-[10px] font-mono font-bold text-steel/50">0{i + 1}</div>
                <div className="w-11 h-11 rounded-xl bg-urgent-600/15 border border-urgent-500/30 flex items-center justify-center mb-5 group-hover:bg-urgent-600 group-hover:border-urgent-600 transition-all">
                  <s.icon className="w-5 h-5 text-urgent-400 group-hover:text-white transition-colors" strokeWidth={2.2} />
                </div>
                <h3 className="text-base font-extrabold text-white tracking-tight">{s.title}</h3>
                <p className="mt-2 text-[13px] text-steel leading-relaxed">{s.desc}</p>
                <div className="mt-5 inline-flex items-center gap-1 text-xs font-bold text-urgent-400 uppercase tracking-wider">
                  Détails <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BANNER — high contrast */}
      <section className="relative bg-ink py-16 lg:py-24 border-y border-white/5">
        <div className="absolute inset-0 bg-grid opacity-30" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.25em] text-urgent-400">
                <span className="w-8 h-px bg-urgent-500" />
                Une urgence ?
              </div>
              <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-[56px] font-extrabold text-white tracking-tight leading-[1.02]">
                Un seul appel.<br />
                <span className="text-metal">Une réponse en 35 minutes.</span>
              </h2>
              <p className="mt-5 text-steel text-base lg:text-lg leading-relaxed max-w-xl">
                Notre technicien le plus proche prend la route immédiatement. Devis annoncé avant intervention, garantie sur tous nos travaux.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <a
                  href="tel:+41796430399"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl gradient-urgent text-white font-bold shadow-orange-glow hover:scale-[1.02] active:scale-[0.98] transition-transform"
                >
                  <Phone className="w-5 h-5" strokeWidth={2.5} />
                  079 643 03 99
                </a>
                <a
                  href="https://fixnow.ch"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl bg-white text-ink hover:bg-steel-light font-bold transition-colors"
                >
                  <Calendar className="w-5 h-5" strokeWidth={2.5} />
                  Voir notre disponibilité sur FixNow
                </a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/[0.03] backdrop-blur rounded-2xl p-6 border border-white/10">
                  <div className="text-4xl lg:text-5xl font-extrabold text-white font-display">15<span className="text-urgent-500">+</span></div>
                  <div className="text-[11px] text-steel mt-2 font-bold uppercase tracking-[0.18em]">Ans d'expérience</div>
                </div>
                <div className="bg-white/[0.03] backdrop-blur rounded-2xl p-6 border border-white/10">
                  <div className="text-4xl lg:text-5xl font-extrabold text-white font-display">5k<span className="text-urgent-500">+</span></div>
                  <div className="text-[11px] text-steel mt-2 font-bold uppercase tracking-[0.18em]">Interventions</div>
                </div>
                <div className="bg-white/[0.03] backdrop-blur rounded-2xl p-6 border border-white/10">
                  <div className="text-4xl lg:text-5xl font-extrabold text-white font-display">35<span className="text-urgent-500">′</span></div>
                  <div className="text-[11px] text-steel mt-2 font-bold uppercase tracking-[0.18em]">Délai moyen</div>
                </div>
                <div className="bg-white/[0.03] backdrop-blur rounded-2xl p-6 border border-white/10">
                  <div className="text-4xl lg:text-5xl font-extrabold text-white font-display">24<span className="text-urgent-500">/7</span></div>
                  <div className="text-[11px] text-steel mt-2 font-bold uppercase tracking-[0.18em]">Disponibilité</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ZONES */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader
            eyebrow="Couverture Suisse romande"
            title="Une présence locale, partout en Romandie."
            subtitle="Genève, Vaud, Neuchâtel, Fribourg, Valais — nous couvrons les principales villes et communes avec des techniciens à proximité."
          />

          <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {zones.map((z) => (
              <Link
                key={z.slug}
                to={`/zones/${z.slug}`}
                className="group flex items-center gap-3 px-5 py-4 rounded-xl bg-white border border-navy-100 hover:border-navy-800 hover:bg-navy-50 transition-all"
              >
                <div className="w-9 h-9 rounded-lg bg-navy-50 group-hover:bg-urgent-50 flex items-center justify-center flex-shrink-0 transition-colors">
                  <MapPin className="w-4 h-4 text-navy-600 group-hover:text-urgent-600 transition-colors" strokeWidth={2.2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-navy-800 text-sm">Plombier {z.city}</div>
                  <div className="text-[11px] text-navy-400 font-semibold">Intervention 24/7</div>
                </div>
                <ArrowRight className="w-4 h-4 text-navy-300 group-hover:text-urgent-600 group-hover:translate-x-0.5 transition-all" />
              </Link>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/zones-intervention"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-navy-50 hover:bg-navy-100 text-navy-700 text-sm font-bold transition-colors"
            >
              Voir toutes les zones d'intervention
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-navy-50 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12 lg:mb-16">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.25em] text-urgent-600">
                <span className="w-8 h-px bg-urgent-600" />
                Avis clients
              </div>
              <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-800 tracking-tight leading-[1.05]">
                Ils nous font confiance.
              </h2>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-urgent-500 text-urgent-500" />
                ))}
              </div>
              <div>
                <div className="text-2xl font-extrabold text-navy-800 font-display leading-none">4.9/5</div>
                <div className="text-xs text-navy-500 font-semibold mt-1">320+ avis vérifiés</div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <div key={i} className="relative bg-white rounded-2xl p-7 border border-navy-100 hover:border-navy-200 hover:shadow-elevated transition-all">
                <Quote className="absolute top-6 right-6 w-10 h-10 text-urgent-100" />
                <div className="flex items-center gap-1">
                  {[...Array(t.rating)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-urgent-500 text-urgent-500" />
                  ))}
                </div>
                <p className="mt-5 text-navy-700 leading-relaxed text-[15px] relative z-10">"{t.text}"</p>
                <div className="mt-7 pt-5 border-t border-navy-100">
                  <div className="font-bold text-navy-800 text-sm">{t.name}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-navy-400 font-semibold">{t.role}</span>
                    <span className="w-1 h-1 rounded-full bg-navy-200" />
                    <span className="text-xs text-navy-400 font-semibold">{t.city}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="bg-white py-20 lg:py-28" id="contact">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <div>
              <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.25em] text-urgent-600">
                <span className="w-8 h-px bg-urgent-600" />
                Contact
              </div>
              <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-800 tracking-tight leading-[1.05]">
                Demandez votre <span className="text-urgent-600">intervention.</span>
              </h2>
              <p className="mt-5 text-base lg:text-lg text-navy-500 leading-relaxed">
                Remplissez le formulaire et nous vous rappelons rapidement. Pour une urgence immédiate, appelez-nous directement — un technicien NEXACO est en route.
              </p>

              <div className="mt-10 space-y-4">
                <a href="tel:+41796430399" className="group flex items-center gap-4 p-5 rounded-2xl bg-navy-900 hover:bg-navy-800 transition-colors">
                  <div className="w-12 h-12 rounded-xl gradient-urgent flex items-center justify-center flex-shrink-0 animate-pulse-ring">
                    <Phone className="w-5 h-5 text-white" strokeWidth={2.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-urgent-400">Urgence 24/7</div>
                    <div className="text-xl font-extrabold text-white font-display">079 643 03 99</div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-white/40 group-hover:text-white group-hover:translate-x-0.5 transition-all" />
                </a>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-4 rounded-2xl bg-navy-50 border border-navy-100">
                    <div className="w-10 h-10 rounded-xl bg-white border border-navy-100 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-4.5 h-4.5 text-navy-700" strokeWidth={2.2} />
                    </div>
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-navy-400">Horaires</div>
                      <div className="text-sm font-bold text-navy-800">24h/24 · 7j/7</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 rounded-2xl bg-navy-50 border border-navy-100">
                    <div className="w-10 h-10 rounded-xl bg-white border border-navy-100 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-4.5 h-4.5 text-emerald-600" strokeWidth={2.2} />
                    </div>
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-navy-400">Membre</div>
                      <div className="text-sm font-bold text-navy-800">Certifié FixNow</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:pl-4">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
